import { memo, MouseEvent, useCallback } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Button } from "@design-system/Button/Button";
import { ArrowCornerIcon } from "@icons";
import { markEventBlocked } from "@design-system/ViewTable/ViewTable.lib";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { HistoryDetailsTableMeta } from "../../HistoryUpdatedTransactionDetails";
import { HistoryCreatedTransactionDetailsRow } from "../views/historyCreatedTransactionDetailsViewConfig";
import styles from "./HistoryTransactionsDetailsCells.module.scss";

type HistoryDrawerCallbacks = NonNullable<HistoryDetailsTableMeta["callbacks"]>;

function rowHasOpenableTransaction(
    row: HistoryCreatedTransactionDetailsRow,
): boolean {
    if (!row.canOpenTransaction) {
        return false;
    }
    return Number.isFinite(Number(row.id));
}

export const HistoryCreatedTransactionActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        HistoryCreatedTransactionDetailsRow,
        string
    >) => {
        const meta = useViewTableMeta<HistoryDrawerCallbacks, unknown>(table);
        const onCreatedRowAction = meta.callbacks?.onCreatedRowAction;

        const handleClick = useCallback(
            (event: MouseEvent<HTMLButtonElement>) => {
                markEventBlocked(event);
                if (!rowHasOpenableTransaction(row.original)) {
                    return;
                }
                onCreatedRowAction?.(row.original);
            },
            [onCreatedRowAction, row.original],
        );

        if (!rowHasOpenableTransaction(row.original)) {
            return <ViewTableCell className={styles.actions} />;
        }

        return (
            <ViewTableCell className={styles.actions}>
                <div className={styles.actionsButtons}>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleClick}
                        title="View details"
                        aria-label="View details"
                        iconOnly
                        className={viewTableStyles.showOnHover}
                    >
                        <ArrowCornerIcon />
                    </Button>
                </div>
            </ViewTableCell>
        );
    },
);

HistoryCreatedTransactionActionsCell.displayName =
    "HistoryCreatedTransactionActionsCell";
